import { useState, useEffect } from 'react';

interface Swap {
  id: string;
  swapper: string;
  tokenIn: string;
  tokenOut: string;
  amountIn: string;
  amountOut: string;
  isYesToNo: boolean;
  isNoToYes: boolean;
  pointsEarned: string;
  timestamp: string;
  transactionHash: string;
}

interface MarketVolumeResult {
  totalVolume: number;
  totalSwaps: number;
  uniqueSwappers: number;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to calculate total trading volume for a specific market/presale
 * This fetches swaps and calculates the real trading volume in USDT
 */
export function useMarketVolume(presaleId: string | undefined): MarketVolumeResult {
  const [data, setData] = useState<MarketVolumeResult>({
    totalVolume: 0,
    totalSwaps: 0,
    uniqueSwappers: 0,
    isLoading: false,
    error: null
  });

  useEffect(() => {
    if (!presaleId) {
      setData({
        totalVolume: 0,
        totalSwaps: 0,
        uniqueSwappers: 0,
        isLoading: false,
        error: 'Missing presale ID'
      });
      return;
    }

    fetchMarketVolume(presaleId);
  }, [presaleId]);

  const fetchMarketVolume = async (presaleId: string) => {
    setData(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const SUBGRAPH_URL = (import.meta as any).env?.VITE_SUBGRAPH_URL;
      if (!SUBGRAPH_URL) {
        throw new Error('Subgraph URL not configured');
      }

      // For now, get all swaps since we don't have presale-specific filtering yet
      // TODO: Once subgraph is properly linked, filter by presaleId
      const swapsQuery = `
        query GetSwaps($first: Int = 1000) {
          swaps(orderBy: timestamp, orderDirection: desc, first: $first) {
            id
            swapper
            tokenIn
            tokenOut
            amountIn
            amountOut
            isYesToNo
            isNoToYes
            pointsEarned
            timestamp
            transactionHash
          }
        }
      `;

      const response = await fetch(SUBGRAPH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: swapsQuery,
          variables: { first: 1000 }
        })
      });

      const data = await response.json();

      if (data.errors) {
        throw new Error(data.errors[0].message);
      }

      const swaps: Swap[] = data.data?.swaps || [];
      
      // Calculate total volume by summing USDT amounts from swaps
      let totalVolume = 0;
      let totalSwaps = 0;
      let uniqueSwappers = new Set<string>();

      swaps.forEach(swap => {
        // Only count swaps where USDT is involved (tokenIn or tokenOut)
        // This gives us the actual trading volume in USDT
        const isUsdtIn = swap.tokenIn.toLowerCase().includes('usdt') || swap.tokenIn.toLowerCase().includes('usdc');
        const isUsdtOut = swap.tokenOut.toLowerCase().includes('usdt') || swap.tokenOut.toLowerCase().includes('usdc');
        
        if (isUsdtIn || isUsdtOut) {
          // Convert amount to USDT (18 decimals)
          const usdtAmount = isUsdtIn 
            ? parseFloat(swap.amountIn) / 1e18 
            : parseFloat(swap.amountOut) / 1e18;
          
          totalVolume += usdtAmount;
          totalSwaps += 1;
          uniqueSwappers.add(swap.swapper);
        }
      });

      // For now, return global volume since we can't filter by presale yet
      // TODO: Implement presale-specific filtering once subgraph supports it
      setData({
        totalVolume,
        totalSwaps,
        uniqueSwappers: uniqueSwappers.size,
        isLoading: false,
        error: null
      });

    } catch (error) {
      console.error('Error fetching market volume:', error);
      
      // Fallback: return 0 volume if subgraph fails
      setData({
        totalVolume: 0,
        totalSwaps: 0,
        uniqueSwappers: 0,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch market volume'
      });
    }
  };

  return data;
}

/**
 * Format volume amount for display
 */
export function formatVolume(volume: number): string {
  if (volume >= 1000000) {
    return `$${(volume / 1000000).toFixed(1)}M`;
  } else if (volume >= 1000) {
    return `$${(volume / 1000).toFixed(0)}K`;
  } else if (volume > 0) {
    return `$${volume.toFixed(0)}`;
  }
  return '$0';
}